import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, Crown, ArrowRight, Star, TrendingUp, Shield, Sparkles, AlertTriangle, X } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';

import { fetchMySubscription, switchSubscriptionPlan, fetchPricingPlans, cancelSubscription } from '../redux/slices/paymentSlice';
import { useFeatureAccess } from '../hooks/useFeatureAccess';

import subscriptionBg from '../assets/subscription-bg.jpg';
import SwitchPlanModal from './SwitchPlanModal';
import CancelSubscriptionModal from './CancelSubscriptionModal';

export default function Subscription() {
    const [billingCycle, setBillingCycle] = useState('yearly');


    const [showSwitchModal, setShowSwitchModal] = useState(false);
    const [selectedSwitchPlan, setSelectedSwitchPlan] = useState('Standard');
    const [switchProcessing, setSwitchProcessing] = useState(false);
    const [portalProcessing, setPortalProcessing] = useState(false);
    const [portalError, setPortalError] = useState(null);
    const [showPortalModal, setShowPortalModal] = useState(false);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { isAuthenticated } = useSelector((state) => state.user);
    const { subscription, plans } = useSelector((state) => state.payment);
    const { totalAnalyses } = useSelector((state) => state.analysis);
    const { searchLimit } = useFeatureAccess();

    const isLimitReached = totalAnalyses >= searchLimit;

    const singleUsePrice = 49.99;
    const standardPriceMonthly = 39.99;
    const standardPriceYearly = 399.99;
    const proPriceMonthly = 299.99;
    const proPriceYearly = 2999.99;

    const getStandardPrice = () => billingCycle === 'yearly' ? (standardPriceYearly / 12).toFixed(2) : standardPriceMonthly;
    const getProPrice = () => billingCycle === 'yearly' ? (proPriceYearly / 12).toFixed(2) : proPriceMonthly;



    // Fetch subscription status on mount
    useEffect(() => {
        if (isAuthenticated) {
            dispatch(fetchMySubscription());
            dispatch(fetchPricingPlans());
        }
    }, [isAuthenticated, dispatch]);

    // Automatically set billing cycle view based on active subscription
    useEffect(() => {
        if (subscription?.status === 'active' && subscription?.plan?.interval) {
            setBillingCycle(subscription.plan.interval === 'year' ? 'yearly' : 'monthly');
        } else {
            setBillingCycle('yearly');
        }
    }, [subscription]);

    // Check if user has active subscription
    const hasActiveSubscription = subscription?.status === 'active';

    const handleUpgradeClick = (planType) => {
        if (!isAuthenticated) {
            navigate('/auth?mode=login');
        } else if (hasActiveSubscription) {
            setSelectedSwitchPlan(planType);
            setShowSwitchModal(true);
        } else {
            redirectToPayment(planType);
        }
    };

    const handleConfirmSwitch = async () => {
        setSwitchProcessing(true);

        const targetInterval = billingCycle === 'yearly' ? 'year' : 'month';
        const targetPlan = plans.find(p => p.interval === targetInterval && p.name.startsWith(selectedSwitchPlan));

        if (!targetPlan) {
            console.error('Target plan not found for:', selectedSwitchPlan, targetInterval);
            setSwitchProcessing(false);
            return;
        }

        try {
            await dispatch(switchSubscriptionPlan({ planId: targetPlan.id })).unwrap();
            await dispatch(fetchMySubscription());
            setShowSwitchModal(false);
        } catch (error) {
            console.error('Failed to switch plan:', error);
        } finally {
            setSwitchProcessing(false);
        }

    };

    const redirectToPayment = (planType) => {
        const price = planType === 'Professional' ? getProPrice() : planType === 'SingleUse' ? singleUsePrice : getStandardPrice();
        navigate('/payment', {
            state: {
                billingCycle,
                price,
                planName: planType
            }
        });
    };



    const handleCancelSubscription = async () => {
        setPortalProcessing(true);
        setPortalError(null);
        try {
            const response = await dispatch(cancelSubscription()).unwrap();
            if (response?.status === 'success') {
                setShowPortalModal(false);
                dispatch(fetchMySubscription());
            } else {
                setPortalError(response?.message || 'Failed to cancel subscription');
            }
        } catch (error) {
            setPortalError(error || 'Failed to cancel subscription');
            console.error('Failed to cancel subscription:', error);
        } finally {
            setPortalProcessing(false);
        }
    };






    const featureCategories = [
        {
            category: "Core Analysis",
            icon: <TrendingUp className="w-5 h-5 text-white" />,
            features: [
                {
                    name: "Monthly Search Limit",
                    single: "1 Property | one-time",
                    free: "2 Properties | monthly",
                    standard: billingCycle === 'yearly' ? "5 Properties | in each month" : "5 Properties | monthly",
                    pro: billingCycle === 'yearly' ? "50 Properties | in each month" : "50 Properties | monthly"
                },
                { name: "Terrain Slope Analysis", single: true, free: true, standard: true, pro: true },
                { name: "LUF Calculations", single: "Advanced | complete", free: "Basic", standard: "Advanced | complete", pro: "Advanced | complete" },
                { name: "MFA & MDA Calculations", single: "Complete | reports", free: true, standard: "Complete | reports", pro: "Complete | reports" },
                { name: "APN & Parcel Information", single: true, free: true, standard: true, pro: true },
                { name: "CDP Requirements Check", single: true, free: true, standard: true, pro: true },
            ]
        },
        {
            category: "Advanced Tools",
            icon: <Zap className="w-5 h-5 text-white" />,
            features: [
                { name: "Elevation Contours", single: "Detailed | lidar", free: "1st Prop. | unlocked", standard: "Detailed | lidar", pro: "Detailed | lidar" },
                { name: "Interactive Map View", single: true, free: "1st Prop. | unlocked", standard: true, pro: true },
                { name: "Export Reports to PDF", single: true, free: "1st Prop. | unlocked", standard: true, pro: true },
                { name: "Save Property History", single: true, free: "1st Prop. | unlocked", standard: true, pro: true },
                { name: "Comparative Analysis", single: true, free: "1st Prop. | unlocked", standard: true, pro: true },
                { name: "Custom Report Gen.", single: true, free: "1st Prop. | unlocked", standard: true, pro: true },
            ]
        },
        {
            category: "Support & API",
            icon: <Shield className="w-5 h-5 text-white" />,
            features: [
                { name: "API Access", single: false, free: false, standard: false, pro: "Coming Soon" },
                { name: "Customer Support", single: "Standard", free: "Standard", standard: "Priority", pro: "VIP Support" },
            ]
        }
    ];


    const renderCell = (value, variant) => {
        if (typeof value === 'boolean') {
            return value ? (
                <div className="flex justify-center">
                    {variant === 'pro' ? (
                        <div className="bg-blue-100 p-1 rounded-full">
                            <Check className="w-5 h-5 text-blue-600" strokeWidth={4} />
                        </div>
                    ) : variant === 'standard' ? (
                        <div className="bg-slate-100 p-1 rounded-full">
                            <Check className="w-5 h-5 text-slate-600" strokeWidth={4} />
                        </div>
                    ) : (
                        <Check className="w-6 h-6 text-gray-400" strokeWidth={3} />
                    )}
                </div>
            ) : (
                <div className="flex justify-center">
                    <span className="w-4 h-0.5 bg-gray-300 rounded-full"></span>
                </div>
            );
        }

        // Professional multi-line rendering for text values with sub-labels (separated by '|')
        if (typeof value === 'string' && value.includes('|')) {
            const [main, sub] = value.split('|');
            return (
                <div className="flex flex-col items-center text-center">
                    <span className={`text-sm font-black ${variant !== 'free' ? 'text-slate-900' : 'text-slate-400'}`}>
                        {main.trim()}
                    </span>
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter mt-0.5">
                        {sub.trim()}
                    </span>
                </div>
            );
        }

        return (
            <span className={`text-sm font-bold text-center block ${variant !== 'free' ? 'text-gray-900' : 'text-gray-500'}`}>
                {value}
            </span>
        );
    };


    return (
        <div className="py-10 px-4 min-h-screen relative overflow-hidden">
            {/* Background Image and Overlay */}
            <div className="absolute inset-0 z-0">
                <img src={subscriptionBg} alt="Background" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px]"></div>
            </div>

            <div className="container mx-auto relative z-10">
                {/* Limit Reached Warning */}
                {isLimitReached && (
                    <div className="mb-12 animate-slide-up">
                        <div className="relative group overflow-hidden bg-white/80 backdrop-blur-md border-2 border-amber-400 rounded-3xl p-1 shadow-2xl shadow-amber-200/50">
                            <div className="absolute inset-0 bg-gradient-to-r from-amber-50 to-orange-50 opacity-50"></div>
                            <div className="relative flex flex-col md:flex-row items-center gap-6 p-6">
                                <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center flex-shrink-0 animate-pulse">
                                    <AlertTriangle className="w-8 h-8 text-amber-600" />
                                </div>
                                <div className="flex-1 text-center md:text-left">
                                    <h3 className="text-xl font-black text-gray-900 mb-1 uppercase tracking-tight">
                                        {subscription?.plan?.name?.startsWith('Professional')
                                            ? `Pro ${subscription.plan.interval === 'year' ? 'Yearly' : 'Monthly'}`
                                            : subscription?.plan?.name?.startsWith('Standard')
                                                ? `Standard ${subscription.plan.interval === 'year' ? 'Yearly' : 'Monthly'}`
                                                : 'Plan'} Search Limit Reached
                                    </h3>
                                    <p className="text-gray-600 font-medium">
                                        You've used all <span className="font-bold text-amber-700">{searchLimit}</span> searches for this month on your {subscription?.plan?.name?.startsWith('Professional') ? 'Pro' : subscription?.plan?.name?.startsWith('Standard') ? 'Standard' : 'Free'} {subscription?.plan?.interval === 'year' ? 'Yearly' : 'Monthly'} plan. Upgrade to increase your monthly limit.
                                    </p>
                                </div>
                                <button
                                    onClick={() => window.scrollTo({ top: 500, behavior: 'smooth' })}
                                    className="px-8 py-4 bg-amber-500 text-white font-black rounded-2xl shadow-lg shadow-amber-200 hover:bg-amber-600 transition-all hover:scale-105 active:scale-95"
                                >
                                    Explore Plans
                                </button>
                            </div>
                        </div>
                    </div>
                )}


                {/* HEADER */}
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-8">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider mb-8 shadow-lg shadow-blue-200/50">
                            <Sparkles className="w-4 h-4 text-white" />
                            <span className="text-white">Transparent Pricing</span>
                        </div>
                        <h1 className="text-6xl md:text-7xl font-extrabold tracking-tight mb-2 leading-tight drop-shadow-sm text-gray-900">
                            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
                                {hasActiveSubscription ? 'Your Current Plan' : 'Unlock Premium'}
                            </span>
                            <br />
                            {hasActiveSubscription ? 'Benefits' : 'Features Today'}
                        </h1>
                        <p className="text-xl text-gray-500 max-w-lg leading-relaxed font-medium">
                            {hasActiveSubscription
                                ? 'Enjoy unlimited access to professional development analysis tools.'
                                : 'Professional grade development analysis tools designed for serious investors and developers.'
                            }
                        </p>
                    </div>

                    {/* Billing Control */}
                    <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-2xl blur opacity-20"></div>
                        <div className="relative flex flex-col items-end gap-1">
                            {hasActiveSubscription && (
                                <span className="text-[10px] text-gray-500 font-semibold uppercase tracking-widest pr-1">Compare intervals</span>
                            )}
                            <div className="flex items-center bg-white/80 backdrop-blur-sm rounded-2xl p-1.5 border border-gray-200/50 self-start md:self-end shadow-xl">
                                <button
                                    onClick={() => setBillingCycle('monthly')}
                                    className={`px-7 py-3 rounded-xl text-sm font-bold transition-all duration-300 ${billingCycle === 'monthly' ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-200' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}
                                >
                                    Monthly
                                </button>
                                <button
                                    onClick={() => setBillingCycle('yearly')}
                                    className={`px-7 py-3 rounded-xl text-sm font-bold transition-all duration-300 flex items-center gap-2 ${billingCycle === 'yearly' ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-200' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}
                                >
                                    Yearly
                                    <span className="bg-emerald-400 text-white text-[10px] px-2.5 py-1 rounded-md font-black shadow-sm">-20%</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* DESKTOP GRID */}
                <div className="hidden lg:block relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-100/20 via-purple-100/20 to-blue-100/20 rounded-3xl blur-2xl"></div>
                    <div className="relative bg-white/70 backdrop-blur-xl rounded-3xl overflow-hidden shadow-2xl border border-gray-200/50">

                        {/* ACTIVE SUBSCRIPTION DETAILS */}
                        {hasActiveSubscription && (
                            <div className="border-b border-indigo-100/80 bg-gradient-to-r from-white via-indigo-50/30 to-purple-50/20 p-8 flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative overflow-hidden">
                                <div className="absolute top-0 right-1/4 w-72 h-72 bg-purple-200/20 rounded-full blur-3xl pointer-events-none"></div>
                                <div className="absolute bottom-0 left-10 w-48 h-48 bg-blue-100/20 rounded-full blur-2xl pointer-events-none"></div>

                                {portalError && (
                                    <div className="relative z-10 w-full bg-rose-50 border border-rose-200 text-rose-700 px-5 py-4 rounded-2xl flex items-center justify-between gap-4 text-sm font-semibold shadow-sm">
                                        <div className="flex items-center gap-2">
                                            <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0" />
                                            <span>{portalError}</span>
                                        </div>
                                        <button
                                            onClick={() => setPortalError(null)}
                                            className="text-rose-400 hover:text-rose-600 transition-colors p-1 rounded-lg hover:bg-rose-100"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}

                                <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-8 flex-grow">
                                    <div className="flex items-center gap-5">
                                        <div className="relative flex-shrink-0">
                                            <div className="absolute inset-0 bg-amber-400/30 rounded-2xl blur-md animate-pulse"></div>
                                            <div className="relative flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 via-yellow-400 to-orange-500 text-white shadow-xl shadow-amber-500/10 border border-white/20">
                                                <Crown className="w-7 h-7 fill-white drop-shadow-sm" strokeWidth={2.2} />
                                            </div>
                                        </div>
                                        <div className="text-left">
                                            <span className="text-[10px] font-black uppercase tracking-widest text-indigo-500 bg-indigo-50 px-2.5 py-1 rounded-md border border-indigo-100/40 inline-block mb-1.5 shadow-sm">
                                                ✦ Active Account
                                            </span>
                                            <h2 className="text-3xl font-black text-slate-800 tracking-tight leading-none">
                                                {subscription?.plan?.name || 'Standard Monthly'}
                                            </h2>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-4">
                                        <div className="flex items-center gap-2.5 bg-emerald-500/5 border border-emerald-500/20 rounded-2xl px-4 py-2.5 shadow-sm">
                                            <span className="relative flex h-2.5 w-2.5">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                                            </span>
                                            <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wide">Status:</span>
                                            <span className="text-xs font-black text-emerald-600 capitalize tracking-wide">{subscription?.status}</span>
                                        </div>
                                        <div className="flex items-center gap-2.5 bg-indigo-500/5 border border-indigo-500/20 rounded-2xl px-4 py-2.5 shadow-sm">
                                            <Shield className="w-4 h-4 text-indigo-500" />
                                            <span className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wide">Renews:</span>
                                            <span className="text-xs font-black text-indigo-600 tracking-wide">
                                                {subscription?.current_period_end
                                                    ? new Date(subscription.current_period_end).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                                                    : '—'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => setShowPortalModal(true)}
                                    className="relative z-10 overflow-hidden px-6 py-3.5 border border-rose-200/80 bg-rose-50/70 text-rose-700 hover:text-white hover:bg-rose-600 hover:border-rose-600 font-black text-[11px] uppercase tracking-wider rounded-2xl shadow-sm hover:shadow-lg hover:shadow-rose-500/20 transition-all duration-300 hover:scale-[1.03] active:scale-[0.97] flex items-center justify-center gap-2 flex-shrink-0 cursor-pointer group"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                                    <span>Cancel Subscription</span>
                                    <X className="w-3.5 h-3.5 group-hover:rotate-90 transition-transform duration-300 text-rose-400 group-hover:text-white" />
                                </button>
                            </div>
                        )}

                        <div className="grid grid-cols-5">

                            {/* HEADERS */}
                            <div className="col-span-1 p-6 flex items-center border-b border-gray-200/50 bg-gradient-to-br from-white to-gray-50/50">
                                <div>
                                    <div className="w-16 h-1.5 bg-gradient-to-r from-blue-500 to-purple-500 mb-5 rounded-full shadow-sm"></div>
                                    <span className="font-bold text-3xl bg-gradient-to-br from-gray-900 to-gray-700 bg-clip-text text-transparent leading-tight">Compare<br />Plans</span>
                                </div>
                            </div>

                            {/* Free Column Header */}
                            <div className="col-span-1 bg-gradient-to-br from-gray-50 to-white p-6 flex flex-col justify-end border-b border-l border-gray-200/50 relative">
                                {subscription?.plan?.name?.startsWith('Free') && (
                                    <div className="absolute top-0 right-0 z-20 overflow-hidden w-24 h-24 pointer-events-none">
                                        <div className="absolute top-5 -right-6 w-28 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[8px] font-black uppercase tracking-widest text-center shadow-lg" style={{ transform: 'rotate(45deg)' }}>
                                            ✦ Your Plan
                                        </div>
                                    </div>
                                )}
                                <div className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wide mb-5 self-start shadow-md border border-gray-200">
                                    Basic
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-gray-900">Free</h3>
                                <div className="flex items-baseline gap-1 mb-4">
                                    <span className="text-4xl font-bold tracking-tight bg-gradient-to-br from-gray-900 to-gray-700 bg-clip-text text-transparent">$0</span>
                                    <span className="text-xs font-semibold text-gray-500">/month</span>
                                </div>
                                <div className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-tight border border-indigo-100 self-start">
                                    <Sparkles className="w-3 h-3" />
                                    1st Prop. Pro
                                </div>
                            </div>

                            {/* Single Use Column Header */}
                            <div className="col-span-1 p-6 flex flex-col justify-end border-b border-l border-gray-200/50 bg-amber-50/30 relative overflow-hidden">
                                <div className="inline-flex items-center gap-2 bg-amber-100 px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wide mb-5 self-start shadow-sm border border-amber-200 text-amber-800">
                                    Pay As You Go
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-gray-900">Single Use</h3>
                                <div className="flex items-baseline gap-1 mb-1">
                                    <span className="text-4xl font-bold tracking-tight text-amber-900">${singleUsePrice}</span>
                                    <span className="text-xs font-semibold text-gray-600">/property</span>
                                </div>
                                <div className="text-[10px] font-bold text-amber-700 uppercase tracking-widest mb-4">
                                    One-time payment
                                </div>
                            </div>

                            {/* Standard Column Header */}
                            <div className="col-span-1 p-6 flex flex-col justify-end border-b border-l border-gray-200/50 bg-slate-50/30 relative overflow-hidden">
                                {subscription?.plan?.name?.startsWith('Standard') && subscription?.plan?.interval === (billingCycle === 'yearly' ? 'year' : 'month') && (
                                    <div className="absolute top-0 right-0 z-20 overflow-hidden w-24 h-24 pointer-events-none">
                                        <div className="absolute top-5 -right-6 w-28 py-1.5 bg-gradient-to-r from-slate-700 to-slate-900 text-white text-[8px] font-black uppercase tracking-widest text-center shadow-lg" style={{ transform: 'rotate(45deg)' }}>
                                            ✦ Your Plan
                                        </div>
                                    </div>
                                )}
                                <div className="inline-flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wide mb-5 self-start shadow-sm border border-slate-200 text-slate-700">
                                    Recommended
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-gray-900">Standard {billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}</h3>
                                <div className="flex items-baseline gap-1 mb-1">
                                    <span className="text-4xl font-bold tracking-tight text-slate-900">${getStandardPrice()}</span>
                                    <span className="text-xs font-semibold text-gray-600">/month</span>
                                </div>
                                {billingCycle === 'yearly' && (
                                    <div className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mb-4">
                                        Billed annually (${standardPriceYearly})
                                    </div>
                                )}
                            </div>

                            {/* Pro Column Header */}
                            <div className="col-span-1 p-6 flex flex-col justify-end border-b border-l border-gray-200/50 relative overflow-hidden">
                                {subscription?.plan?.name?.startsWith('Professional') && subscription?.plan?.interval === (billingCycle === 'yearly' ? 'year' : 'month') && (
                                    <div className="absolute top-0 right-0 z-20 overflow-hidden w-24 h-24 pointer-events-none">
                                        <div className="absolute top-5 -right-6 w-28 py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-700 text-white text-[10px] font-black uppercase tracking-widest text-center shadow-xl" style={{ transform: 'rotate(45deg)' }}>
                                            ✦ Active
                                        </div>
                                    </div>
                                )}
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50/30 to-blue-50"></div>

                                <div className="relative z-10 mb-5">
                                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wide shadow-lg shadow-blue-200/50 text-white">
                                        Professional
                                    </div>
                                </div>

                                <div className="relative z-10">
                                    <h3 className="text-xl font-bold mb-3 flex items-center gap-2 text-gray-900">
                                        Pro {billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} <Crown className="w-4 h-4 text-blue-600 fill-blue-100" />
                                    </h3>
                                    <div className="flex items-baseline gap-1 mb-1">
                                        <span className="text-4xl font-bold tracking-tight bg-gradient-to-br from-blue-600 to-blue-700 bg-clip-text text-transparent">${getProPrice()}</span>
                                        <span className="text-xs font-semibold text-gray-600">/month</span>
                                    </div>
                                    {billingCycle === 'yearly' && (
                                        <div className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest mb-4">
                                            Billed annually (${proPriceYearly})
                                        </div>
                                    )}
                                </div>
                            </div>


                            {/* FEATURE ROWS */}
                            {featureCategories.map((category, idx) => (
                                <React.Fragment key={idx}>
                                    <div className="col-span-5 bg-gradient-to-r from-gray-200 via-gray-50 to-gray-300 px-6 py-3 flex items-center gap-3 border-b border-gray-400/50">
                                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center shadow-md">
                                            {category.icon}
                                        </div>
                                        <h4 className="font-bold uppercase tracking-wider text-xs text-gray-900">{category.category}</h4>
                                        <div className="flex-1 h-px bg-gradient-to-r from-gray-300 to-transparent"></div>
                                    </div>

                                    {category.features.map((feature, fIdx) => (
                                        <React.Fragment key={fIdx}>
                                            <div className="col-span-1 px-6 py-4 flex items-center border-b border-gray-200/50 bg-gradient-to-r from-white to-gray-50/30 hover:bg-gray-50/50 transition-colors duration-200">
                                                <span className="font-semibold text-gray-800 text-xs">{feature.name}</span>
                                            </div>
                                            <div className="col-span-1 bg-gradient-to-br from-gray-50 to-white px-4 py-4 flex items-center justify-center border-b border-l border-gray-200/50">
                                                {renderCell(feature.free, 'free')}
                                            </div>
                                            <div className="col-span-1 bg-amber-50/20 px-4 py-4 flex items-center justify-center border-b border-l border-gray-200/50">
                                                {renderCell(feature.single, 'single')}
                                            </div>
                                            <div className="col-span-1 bg-slate-50/30 px-4 py-4 flex items-center justify-center border-b border-l border-gray-200/50">
                                                {renderCell(feature.standard, 'standard')}
                                            </div>
                                            <div className="col-span-1 px-4 py-4 flex items-center justify-center border-b border-l border-gray-200/50 bg-gradient-to-br from-blue-50/40 via-purple-50/20 to-blue-50/40 hover:from-blue-50/60 hover:to-purple-50/30 transition-all duration-300 group relative">
                                                {renderCell(feature.pro, 'pro')}
                                            </div>
                                        </React.Fragment>
                                    ))}

                                </React.Fragment>
                            ))}

                            {/* FOOTER */}
                            <div className="col-span-1 px-6 py-6 flex items-center bg-gradient-to-br from-white to-gray-50/50">
                                <span className="font-bold text-gray-900 text-sm">Get Started</span>
                            </div>
                            <div className="col-span-1 bg-gradient-to-br from-gray-50 to-white p-6 border-l border-gray-200/50">
                                <button disabled className="w-full py-3 bg-gray-100 text-gray-500 text-xs font-bold rounded-xl border border-gray-200">
                                    {!hasActiveSubscription ? 'Current Plan' : 'Free Tier'}
                                </button>
                            </div>
                            {/* Single Use CTA */}
                            <div className="col-span-1 bg-amber-50/20 p-6 border-l border-gray-200/50">
                                <button onClick={() => redirectToPayment('SingleUse')} className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all shadow-md shadow-amber-200">
                                    Buy Single Use (${singleUsePrice})
                                </button>
                            </div>
                            {/* Standard CTA */}
                            <div className="col-span-1 bg-slate-50/30 p-6 border-l border-gray-200/50">
                                {subscription?.plan?.name?.startsWith('Standard') ? (
                                    subscription?.plan?.interval === (billingCycle === 'yearly' ? 'year' : 'month') ? (
                                        <div title="This is your active plan" className="w-full py-3 text-center bg-gradient-to-r from-slate-800 to-slate-900 text-white font-bold rounded-xl flex items-center justify-center gap-1.5 shadow-md cursor-default select-none text-xs">
                                            <Crown className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> <span>Standard Active</span>
                                        </div>
                                    ) : (
                                        <button onClick={() => handleUpgradeClick('Standard')} className="w-full py-3 bg-emerald-600 text-white text-xs font-bold rounded-xl hover:bg-emerald-700 transition-all flex items-center justify-center gap-1">
                                            Switch to Standard
                                        </button>
                                    )
                                ) : subscription?.plan?.name?.startsWith('Professional') ? (
                                    <button onClick={() => handleUpgradeClick('Standard')} className="w-full py-3 bg-amber-500 text-white text-xs font-bold rounded-xl hover:bg-amber-600 transition-all flex items-center justify-center gap-1">
                                        ↓ Downgrade
                                    </button>
                                ) : (
                                    <button onClick={() => handleUpgradeClick('Standard')} className="w-full py-3 bg-slate-900 text-white text-xs font-bold rounded-xl hover:bg-slate-800 transition-all">
                                        Upgrade to Standard
                                    </button>
                                )}
                            </div>

                            {/* Pro CTA */}
                            <div className="col-span-1 p-6 border-l border-gray-200/50 bg-gradient-to-br from-blue-50/40 to-purple-50/30">
                                {subscription?.plan?.name?.startsWith('Professional') ? (
                                    subscription?.plan?.interval === (billingCycle === 'yearly' ? 'year' : 'month') ? (
                                        <div title="This is your active plan" className="w-full py-3 text-center bg-gradient-to-r from-indigo-600 to-blue-500 text-white font-bold rounded-xl flex items-center justify-center gap-1.5 shadow-md shadow-blue-300/40 cursor-default select-none text-xs">
                                            <Crown className="w-3.5 h-3.5 fill-amber-300 text-amber-300" /> <span>Pro Active</span>
                                        </div>
                                    ) : (
                                        <button onClick={() => handleUpgradeClick('Professional')} className="w-full py-3 bg-emerald-600 text-white text-xs font-bold rounded-xl hover:bg-emerald-700 transition-all flex items-center justify-center gap-1">
                                            Switch to Pro
                                        </button>
                                    )
                                ) : (
                                    <button onClick={() => handleUpgradeClick('Professional')} className="w-full py-3 bg-blue-600 text-white text-xs font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md shadow-blue-200">
                                        ↑ Upgrade to Pro
                                    </button>
                                )}
                            </div>

                        </div>
                    </div>
                </div>

                {/* MOBILE STACK */}
                <div className="lg:hidden space-y-8">
                    {/* ACTIVE SUBSCRIPTION DETAILS & PORTAL BANNER (MOBILE) */}
                    {hasActiveSubscription && (
                        <div className="relative group rounded-3xl overflow-hidden p-[1px] bg-gradient-to-r from-blue-200 via-indigo-200 to-purple-300 shadow-xl transition-all duration-300">
                            <div className="relative bg-white/95 backdrop-blur-xl text-slate-800 rounded-[23px] p-6 flex flex-col gap-6 overflow-hidden">
                                {/* Ambient Background Glow */}
                                <div className="absolute -top-10 -right-10 w-36 h-36 bg-purple-200/30 rounded-full blur-2xl pointer-events-none"></div>

                                {portalError && (
                                    <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-2xl flex items-center justify-between gap-3 text-xs font-semibold shadow-sm animate-shake">
                                        <div className="flex items-center gap-2">
                                            <AlertTriangle className="w-4 h-4 text-rose-500 flex-shrink-0" />
                                            <span>{portalError}</span>
                                        </div>
                                        <button onClick={() => setPortalError(null)} className="text-rose-400 hover:text-rose-600">
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                )}

                                <div className="flex items-center gap-4 relative z-10">
                                    <div className="relative flex-shrink-0">
                                        <div className="absolute inset-0 bg-amber-400/30 rounded-xl blur-sm animate-pulse"></div>
                                        <div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 via-yellow-400 to-orange-500 text-white shadow-lg shadow-amber-500/10 border border-white/20">
                                            <Crown className="w-6 h-6 fill-white" strokeWidth={2.2} />
                                        </div>
                                    </div>
                                    <div className="text-left">
                                        <span className="text-[8px] font-black uppercase tracking-widest text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100/40 inline-block mb-1 shadow-sm">
                                            ✦ Active Account
                                        </span>
                                        <h3 className="text-xl font-black text-slate-800 tracking-tight leading-none">
                                            {subscription?.plan?.name || 'Standard Monthly'}
                                        </h3>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-3 relative z-10">
                                    <div className="flex items-center gap-2.5 bg-emerald-500/5 border border-emerald-500/10 rounded-xl px-4 py-2.5 text-xs shadow-sm">
                                        <span className="relative flex h-2 w-2">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                        </span>
                                        <span className="text-slate-400 font-extrabold uppercase text-[9px] tracking-wide">Status:</span>
                                        <span className="text-emerald-600 font-black tracking-wide capitalize">{subscription?.status}</span>
                                    </div>
                                    <div className="flex items-center gap-2.5 bg-indigo-500/5 border border-indigo-500/10 rounded-xl px-4 py-2.5 text-xs shadow-sm">
                                        <Shield className="w-4 h-4 text-indigo-500" />
                                        <span className="text-slate-400 font-extrabold uppercase text-[9px] tracking-wide">Renews:</span>
                                        <span className="text-indigo-600 font-black tracking-wide">
                                            {subscription?.current_period_end
                                                ? new Date(subscription.current_period_end).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                                                : '—'}
                                        </span>
                                    </div>
                                </div>

                                <button
                                    onClick={() => setShowPortalModal(true)}
                                    className="w-full py-4 border border-rose-200/80 bg-rose-50/70 text-rose-700 hover:text-white hover:bg-rose-600 hover:border-rose-600 font-black text-[10px] uppercase tracking-wider rounded-2xl shadow-sm hover:shadow-lg hover:shadow-rose-500/20 transition-all duration-300 hover:scale-[1.03] active:scale-[0.97] flex items-center justify-center gap-2 relative z-10 group cursor-pointer overflow-hidden"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                                    <span>Cancel Subscription</span>
                                    <X className="w-3.5 h-3.5 group-hover:rotate-90 transition-transform duration-300 text-rose-400 group-hover:text-white" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Free Mobile */}
                    <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-gray-100/50 to-transparent rounded-3xl blur-xl"></div>
                        <div className="relative bg-white/70 backdrop-blur-xl rounded-3xl p-8 shadow-xl border border-gray-200/50">
                            {subscription?.plan?.name?.startsWith('Free') && (
                                <div className="absolute top-6 right-6 bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border border-emerald-200 flex items-center gap-1.5 shadow-sm">
                                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                                    Active
                                </div>
                            )}
                            <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wide mb-5 shadow-md border border-gray-200">
                                Basic
                            </div>
                            <h3 className="text-4xl font-bold text-gray-900 mb-4">Free</h3>
                            <div className="text-6xl font-bold bg-gradient-to-br from-gray-900 to-gray-700 bg-clip-text text-transparent mb-4">$0</div>
                            <div className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-tight border border-indigo-100 mb-8 self-start">
                                <Sparkles className="w-3 h-3" />
                                1st Property Pro Features
                            </div>

                            <div className="space-y-4 mb-10">
                                {featureCategories.map(cat => cat.features).flat().slice(0, 6).map((feature, i) => (
                                    <div key={i} className="flex justify-between items-center py-4 border-b border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-transparent rounded-lg px-2">
                                        <span className="font-semibold text-gray-700 text-sm">{feature.name}</span>
                                        <span className="text-sm font-bold text-gray-900">{typeof feature.free === 'boolean' ? (feature.free ? '✓' : '—') : feature.free}</span>
                                    </div>
                                ))}
                            </div>
                            <button
                                disabled
                                className="w-full py-4 bg-gray-100 text-gray-500 text-base font-bold rounded-xl border border-gray-300 shadow-inner"
                            >
                                {!hasActiveSubscription ? 'Current Plan' : 'Free Tier'}
                            </button>
                        </div>
                    </div>

                    {/* Single Use Mobile */}
                    <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-100/40 to-transparent rounded-3xl blur-2xl"></div>
                        <div className="relative bg-white/70 backdrop-blur-xl rounded-3xl p-8 shadow-xl border border-amber-200/50">
                            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wide mb-5 border border-amber-200">
                                Pay As You Go
                            </div>
                            <h3 className="text-4xl font-bold text-gray-900 mb-4">Single Use</h3>
                            <div className="text-6xl font-bold text-amber-900 mb-1">${singleUsePrice}</div>
                            <div className="text-sm font-bold text-gray-600 mb-10">per property report (one-time)</div>

                            <div className="space-y-4 mb-10">
                                {featureCategories.map(cat => cat.features).flat().slice(0, 6).map((feature, i) => (
                                    <div key={i} className="flex justify-between items-center py-4 border-b border-amber-200/50 px-2">
                                        <span className="font-semibold text-gray-800 text-sm">{feature.name}</span>
                                        <span className="text-sm font-bold text-gray-900">{typeof feature.single === 'boolean' ? (feature.single ? '✓' : '—') : feature.single}</span>
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={() => redirectToPayment('SingleUse')}
                                className="w-full py-5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-lg font-bold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg shadow-amber-200"
                            >
                                Buy Single Use (${singleUsePrice})
                            </button>
                        </div>
                    </div>

                    {/* Standard Mobile */}
                    <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-slate-200/40 to-transparent rounded-3xl blur-2xl"></div>
                        <div className="relative bg-white/70 backdrop-blur-xl rounded-3xl p-8 shadow-xl border border-slate-200/50">
                            {subscription?.plan?.name?.startsWith('Standard') && subscription?.plan?.interval === (billingCycle === 'yearly' ? 'year' : 'month') && (
                                <div className="absolute top-0 right-0 z-20 overflow-hidden w-24 h-24 pointer-events-none">
                                    <div className="absolute top-5 -right-6 w-28 py-1.5 bg-gradient-to-r from-slate-700 to-slate-900 text-white text-[8px] font-black uppercase tracking-widest text-center shadow-lg" style={{ transform: 'rotate(45deg)' }}>
                                        ✦ Your Plan
                                    </div>
                                </div>
                            )}
                            <div className="absolute top-6 right-6 bg-slate-100 text-slate-600 text-xs font-bold px-4 py-2 rounded-xl border border-slate-200">
                                Recommended
                            </div>

                            <h3 className="text-4xl font-bold text-gray-900 mb-4">Standard {billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}</h3>
                            <div className="text-6xl font-bold text-slate-900 mb-3">${getStandardPrice()}</div>
                            <div className="text-sm font-bold text-gray-600 mb-10">billed {billingCycle}</div>

                            <div className="space-y-4 mb-10">
                                {featureCategories.map(cat => cat.features).flat().slice(0, 6).map((feature, i) => (
                                    <div key={i} className="flex justify-between items-center py-4 border-b border-slate-200/50 px-2">
                                        <span className="font-semibold text-gray-800 text-sm">{feature.name}</span>
                                        <span className="text-sm font-bold text-gray-900">{typeof feature.standard === 'boolean' ? (feature.standard ? '✓' : '—') : feature.standard}</span>
                                    </div>
                                ))}
                            </div>

                            {subscription?.plan?.name?.startsWith('Standard') ? (
                                subscription?.plan?.interval === (billingCycle === 'yearly' ? 'year' : 'month') ? (
                                    <div className="w-full py-5 bg-gradient-to-r from-slate-800 to-slate-900 text-white text-base font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg">
                                        <Crown className="w-5 h-5 fill-amber-400 text-amber-400" /> Standard {billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} Active <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
                                    </div>
                                ) : (
                                    <button onClick={() => handleUpgradeClick('Standard')} className="w-full py-5 bg-emerald-600 text-white text-lg font-bold rounded-xl hover:bg-emerald-700 transition-all flex items-center justify-center gap-3">
                                        Switch to Standard {billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
                                    </button>
                                )
                            ) : subscription?.plan?.name?.startsWith('Professional') ? (
                                <button onClick={() => handleUpgradeClick('Standard')} className="w-full py-5 bg-amber-500 text-white text-lg font-bold rounded-xl hover:bg-amber-600 transition-all flex items-center justify-center gap-3">
                                    ↓ Downgrade to Standard
                                </button>
                            ) : (
                                <button onClick={() => handleUpgradeClick('Standard')} className="w-full py-5 bg-slate-900 text-white text-lg font-bold rounded-xl hover:bg-slate-800 transition-all">
                                    Upgrade to Standard
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Pro Mobile */}
                    <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-200/40 via-purple-200/30 to-blue-200/40 rounded-3xl blur-2xl"></div>
                        <div className="relative bg-gradient-to-br from-white via-blue-50/30 to-white backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-blue-200/50">
                            {subscription?.plan?.name?.startsWith('Professional') && subscription?.plan?.interval === (billingCycle === 'yearly' ? 'year' : 'month') && (
                                <div className="absolute top-0 right-0 z-20 overflow-hidden w-24 h-24 pointer-events-none">
                                    <div className="absolute top-5 -right-6 w-28 py-1.5 bg-gradient-to-r from-indigo-600 to-blue-500 text-white text-[8px] font-black uppercase tracking-widest text-center shadow-xl" style={{ transform: 'rotate(45deg)' }}>
                                        ✦ Your Plan
                                    </div>
                                </div>
                            )}
                            <div className="absolute top-6 right-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-bold px-4 py-2 rounded-xl shadow-lg shadow-blue-200/50">
                                <div className="flex items-center gap-1.5">
                                    <Star className="w-3.5 h-3.5 fill-white" />
                                    Professional
                                </div>
                            </div>

                            <h3 className="text-4xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                                Pro {billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} <Crown className="w-8 h-8 text-blue-600 fill-blue-100" />
                            </h3>
                            <div className="text-6xl font-bold bg-gradient-to-br from-blue-600 to-blue-700 bg-clip-text text-transparent mb-3">${getProPrice()}</div>
                            <div className="text-sm font-bold text-gray-600 mb-10">billed {billingCycle}</div>

                            <div className="space-y-4 mb-10">
                                {featureCategories.map(cat => cat.features).flat().slice(0, 6).map((feature, i) => (
                                    <div key={i} className="flex justify-between items-center py-4 border-b border-blue-200/50 bg-gradient-to-r from-blue-50/50 to-transparent rounded-lg px-2 hover:from-blue-50 transition-colors duration-200">
                                        <span className="font-semibold text-gray-800 text-sm">{feature.name}</span>
                                        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-2 shadow-lg shadow-blue-200/50">
                                            <Check className="w-4 h-4" strokeWidth={3} />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {subscription?.plan?.name?.startsWith('Professional') ? (
                                subscription?.plan?.interval === (billingCycle === 'yearly' ? 'year' : 'month') ? (
                                    <div className="w-full py-5 bg-gradient-to-r from-indigo-600 to-blue-500 text-white text-base font-bold rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-blue-300/40">
                                        <Crown className="w-5 h-5 fill-amber-300 text-amber-300" /> Pro {billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} Active <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => handleUpgradeClick('Professional')}
                                        className="w-full py-5 bg-emerald-600 hover:bg-emerald-700 text-white text-lg font-bold rounded-xl transition-all duration-300 hover:shadow-2xl hover:scale-105 flex items-center justify-center gap-3 group relative overflow-hidden"
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                                        <span className="relative">Switch to Pro {billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}</span>
                                        <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300 relative" />
                                    </button>
                                )
                            ) : (
                                <button
                                    onClick={() => handleUpgradeClick('Professional')}
                                    className="w-full py-5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-lg font-bold rounded-xl transition-all duration-300 hover:shadow-2xl hover:shadow-blue-300/50 hover:scale-105 flex items-center justify-center gap-3 group relative overflow-hidden"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                                    <span className="relative">↑ Upgrade to Pro {billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}</span>
                                    <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300 relative" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Plan Switch Confirmation Modal */}
                <SwitchPlanModal
                    isOpen={showSwitchModal}
                    onClose={() => setShowSwitchModal(false)}
                    selectedSwitchPlan={selectedSwitchPlan}
                    billingCycle={billingCycle}
                    proPriceYearly={proPriceYearly}
                    proPriceMonthly={proPriceMonthly}
                    standardPriceYearly={standardPriceYearly}
                    standardPriceMonthly={standardPriceMonthly}
                    onConfirm={handleConfirmSwitch}
                    processing={switchProcessing}
                    subscription={subscription}
                />

                {/* Cancel Subscription Confirmation Modal */}
                <AnimatePresence>
                    {showPortalModal && (
                        <CancelSubscriptionModal
                            isOpen={showPortalModal}
                            onClose={() => setShowPortalModal(false)}
                            subscription={subscription}
                            onConfirm={handleCancelSubscription}
                            processing={portalProcessing}
                        />
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}